import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SaveOpportunityInput, SavedListQuery } from '@teentack/shared';
import { jobKeys } from '@/features/jobs/use-jobs';
import { fetchShortlist, saveOpportunity, unsaveOpportunity } from './shortlist.api';

export const shortlistKeys = {
  all: ['shortlist'] as const,
  list: (query: Partial<SavedListQuery>) => ['shortlist', 'list', query] as const,
};

/**
 * The caller's shortlist, or — for a guardian passing `teenId` — their teen's.
 * [Teen step 8, Parent step 7]
 */
export function useShortlist(query: Partial<SavedListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: shortlistKeys.list(query),
    queryFn: () => fetchShortlist(query),
    enabled,
  });
}

/**
 * Toggling an entry also invalidates the job lists, because every job card
 * carries a `savedByViewer` flag the server computed.
 */
export function useToggleShortlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveOpportunityInput & { saved: boolean }) => {
      if (input.saved) {
        await unsaveOpportunity(input.jobId);
        return;
      }
      await saveOpportunity({
        jobId: input.jobId,
        ...(input.savedForTeenId ? { savedForTeenId: input.savedForTeenId } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shortlistKeys.all });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}
